'use client';

import React from 'react';
import { X, CheckCheck, Phone, Video, MoreVertical, Smartphone } from 'lucide-react';
import { WhatsAppMessageItem } from './WhatsAppMessagesList';

interface WhatsAppMessagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: WhatsAppMessageItem | null;
  businessName?: string;
}

export function WhatsAppMessagePreviewModal({
  isOpen,
  onClose,
  message,
  businessName = 'Juan Rojas Barbería'
}: WhatsAppMessagePreviewModalProps) {
  if (!isOpen || !message) return null;

  // Reemplazar variables con datos realistas
  const renderedContent = message.content
    .replace(/\{\{cliente\}\}/g, 'Gonzalo')
    .replace(/\{\{servicio\}\}/g, 'Corte clásico / Degradé')
    .replace(/\{\{fecha\}\}/g, 'Martes 14 de Octubre')
    .replace(/\{\{hora\}\}/g, '15:30 hs')
    .replace(/\{\{negocio\}\}/g, businessName);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs transition-opacity animate-in fade-in">
      <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden border border-slate-100 flex flex-col max-h-[92vh]">
        {/* Modal Top Bar */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
          <div>
            <h3 className="text-sm font-bold text-slate-900">{message.title}</h3>
            <p className="text-[11px] text-slate-500">Vista previa de cómo lo recibe el cliente</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Smartphone Chat Screen Simulation */}
        <div className="p-4 bg-slate-100/80 flex justify-center">
          <div className="w-full rounded-2xl overflow-hidden shadow-md border border-slate-300 flex flex-col bg-[#efeae2] relative min-h-[380px]">
            {/* WhatsApp Header */}
            <div className="bg-[#075e54] text-white px-3.5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-emerald-700 text-white font-bold text-xs flex items-center justify-center border border-white/30">
                  JR
                </div>
                <div>
                  <h4 className="text-xs font-bold leading-tight">{businessName}</h4>
                  <p className="text-[10px] text-emerald-200 leading-none">en línea</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-white/80">
                <Video className="w-3.5 h-3.5" />
                <Phone className="w-3.5 h-3.5" />
                <MoreVertical className="w-3.5 h-3.5" />
              </div>
            </div>

            {/* Chat Body */}
            <div className="p-3.5 flex-1 flex flex-col justify-end space-y-2">
              <div className="self-center bg-white/80 text-slate-600 px-2.5 py-1 rounded-md text-[10px] font-semibold shadow-2xs">
                HOY
              </div>

              {/* Message Bubble */}
              <div className="self-start max-w-[88%] bg-white rounded-2xl rounded-tl-xs p-3.5 shadow-sm space-y-1 relative border border-slate-200/60">
                <p className="text-xs text-slate-800 leading-relaxed whitespace-pre-line">
                  {renderedContent}
                </p>

                <div className="flex items-center justify-end gap-1 pt-1 text-[10px] text-slate-400">
                  <span>14:02</span>
                  <CheckCheck className="w-3.5 h-3.5 text-sky-500" />
                </div>
              </div>
            </div>

            {/* Bottom Fake Input */}
            <div className="p-2 bg-slate-200/60 flex items-center gap-2">
              <div className="flex-1 bg-white px-3 py-1.5 rounded-full text-[11px] text-slate-400">
                Mensaje
              </div>
              <div className="w-7 h-7 rounded-full bg-[#128c7e] text-white flex items-center justify-center text-xs">
                🎙️
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-white border-t border-slate-100 flex items-center justify-between">
          <span className="text-[11px] text-slate-500">
            Disparador: <strong>{message.trigger || 'Automático'}</strong>
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
